/**
 * API客户端基础配置
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.token = localStorage.getItem('access_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('access_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  setRefreshToken(token: string) {
    localStorage.setItem('refresh_token', token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Try to get error details
        let errorDetail = '';
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || JSON.stringify(errorData);
          console.error('API error details:', errorData);
        } catch {
          // If can't parse JSON, get text
          errorDetail = await response.text();
        }

        if (response.status === 401) {
          // 登录/注册等认证接口的 401 是业务错误，不触发 token 刷新
          const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/send-code');
          if (isAuthEndpoint) {
            throw new Error(`HTTP ${response.status}: ${errorDetail}`);
          }
          // Token expired, try to refresh
          const refreshed = await this.tryRefreshToken();
          if (refreshed) {
            // Retry with new token
            headers['Authorization'] = `Bearer ${this.token}`;
            const retryResponse = await fetch(url, { ...config, headers });
            if (!retryResponse.ok) {
              throw new Error(`HTTP ${retryResponse.status}: ${errorDetail}`);
            }
            return await retryResponse.json();
          } else {
            this.clearToken();
            window.location.href = '/';
          }
        }
        throw new Error(`HTTP ${response.status}: ${errorDetail}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private async tryRefreshToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.code === 0 && data.data) {
          this.setToken(data.data.access_token);
          this.setRefreshToken(data.data.refresh_token);
          return true;
        }
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }

    return false;
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return this.request<T>(url, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

/**
 * 将后端返回的相对静态资源路径转为完整URL
 * - /static/uploads/xxx.jpg → 直接返回相对路径（浏览器自动补全域名）
 * - http://xxx → 原样返回
 * - 其他相对路径 → 拼接后端地址
 */
export function getStaticUrl(path: string | undefined | null): string {
  if (!path) return '';
  // 已经是完整URL，原样返回
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  // /static/ 开头的路径直接返回相对路径，由Nginx处理
  if (path.startsWith('/static/')) return path;
  // 其他相对路径拼接后端地址
  const backendBase = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
  return `${backendBase}${path.startsWith('/') ? '' : '/'}${path}`;
}

export default apiClient;
